<template>
  <div
    :class="['chat-message', type, { 'ai-assistant': isAiTextMessage() }]"
    :data-cy="message.id != null ? `chat-message-${message.id}` : null"
  >
    <div v-if="showDate" class="message-date">
      {{ moment(message.created).format('LL') }}
    </div>
    <div class="message-container">
      <div class="message-header">
        <div v-if="showIcon" class="message-icon">
          <div v-if="isSystemMessage()" class="message-icon">
            <div class="text-icon">S</div>
          </div>
          <div v-else class="text-icon">{{ username.substr(0, 1) }}</div>
        </div>
        <div class="username">{{ username }}</div>
      </div>

      <div v-if="message.text" class="message-content">
        <b-spinner
          v-if="message.pending && message.sender"
          small
          variant="light"
        />
        <!-- v-text, not {{ }} on its own line: pre-wrap renders the template's indent as a leading space. -->
        <div
          v-if="message.text"
          class="message-text"
          v-text="translated(message)"
        />
        <b-spinner
          v-if="message.pending && !message.sender"
          small
          variant="light"
        />

        <div class="message-time">
          <span class="timestamp-text">{{
            moment(message.created).format('LT')
          }}</span>
          <div class="message-status-icons">
            <div
              v-if="
                message.status === 'delivered' || message.status === 'received'
              "
              class="status-icon-wrapper"
              data-tooltip="Delivered"
            >
              <CheckCheck :size="14" class="status-icon" />
            </div>
            <div
              v-if="message.status === 'queued'"
              class="status-icon-wrapper"
              data-tooltip="Queued"
            >
              <Clock :size="14" class="status-icon" />
            </div>
            <div
              v-if="message.status === 'sent' || message.status === 'accepted'"
              class="status-icon-wrapper"
              data-tooltip="Sent"
            >
              <Check :size="14" class="status-icon" />
            </div>
            <div
              v-if="
                message.error ||
                message.status === 'failed' ||
                message.status === 'undelivered'
              "
              class="status-icon-wrapper"
              data-tooltip="Failed to send"
            >
              <CircleAlert :size="14" class="status-icon error" />
            </div>
          </div>
        </div>
      </div>

      <small
        v-if="message.errorMessage || message.error"
        class="errors error-message"
      >
        <div class="error">
          <CircleAlert
            class="message-error"
            :size="16"
            :title="
              message.errorMessage
                ? message.errorMessage
                : 'The message was not sent due to an error'
            "
          />
        </div>
        <div>
          {{
            message.errorMessage
              ? message.errorMessage
              : 'The message was not sent due to an error'
          }}
        </div>
      </small>

      <span v-if="message.attachments && message.attachments.length">
        <span
          v-for="(attachment, index) of message.attachments"
          :key="attachment.url"
        >
          <div v-if="vcard(attachment.type)" class="vcard">
            <a :href="attachment.url">
              <IdCard :size="20" />
            </a>
          </div>
          <div v-else class="attachment">
            <img
              :data-cy="`chat-message-attachment-${index}`"
              :src="attachment.url"
              @click="openImage(attachment.url)"
            />
          </div>
        </span>
      </span>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import moment from 'moment'
import { IdCard, CircleAlert, Check, CheckCheck, Clock } from 'lucide-vue'
import { chatSenderName } from '@/utils/functions'

/**
 * Service accounts are identified by email because the message payload carries no
 * sender-type field — TwilioMessage has only a nullable sender FK, so role + email is
 * all the API exposes (MessageSenderSerializer, api/crm/serializers.py).
 * Both users are global, not per-merchant, and provisioned Django-side from
 * settings.BLYTZCOLLECT_EMAIL / settings.AI_TEXTS_EMAIL.
 */
const AI_TEXTS_EMAIL = 'aitexts@blytzpay.com'
const BLYTZCOLLECT_EMAIL = 'blytzcollect@blytzpay.com'

export default Vue.extend({
  name: 'ChatMessage',
  components: {
    IdCard,
    CircleAlert,
    Check,
    CheckCheck,
    Clock
  },
  props: {
    message: {
      type: Object,
      required: true
    },
    lastMessage: {
      type: Object,
      required: false
    },
    showTime: {
      type: Boolean,
      default: true
    },
    showName: {
      type: Boolean,
      default: true
    },
    type: {
      type: String,
      required: true
    },
    showIcon: {
      type: Boolean,
      default: true
    },
    showDate: {
      type: Boolean,
      default: false
    },
    customer: {
      type: Object,
      required: true
    },
    language: {
      type: String,
      required: false,
      default: 'en-US'
    }
  },
  computed: {
    username(): string {
      const sender = this.message?.sender

      if (sender && typeof sender === 'object') {
        if (this.isAiTextMessage()) {
          return 'AI Assistant'
        }
        if (this.isFromBlytzCollect()) {
          return 'BlytzCollect™'
        }
        if (this.isSystemMessage()) {
          return 'System message'
        }
      }

      return chatSenderName(sender, this.customer)
    }
  },
  methods: {
    moment,
    /**
     * Every named service account must be excluded here, not just ordered ahead of it
     * in `username`. The avatar block calls this on its own, so it has to be correct in
     * isolation.
     */
    isSystemMessage() {
      const sender = this.message?.sender
      if (!sender?.email) return false
      if (sender.email === AI_TEXTS_EMAIL) return false
      if (sender.email === BLYTZCOLLECT_EMAIL) return false

      return Boolean(sender?.roles?.includes('super_admin'))
    },
    isAiTextMessage() {
      const sender = this.message?.sender

      return Boolean(
        sender?.roles?.includes('super_admin') &&
        sender?.email === AI_TEXTS_EMAIL
      )
    },
    openImage(url) {
      window.open(url, '_blank')
    },
    vcard(type) {
      return type === 'text/vcard' || type === 'text/x-vcard'
    },
    translated(message) {
      return this.language == message.language
        ? message.text
        : (message.translations[this.language] ?? message.text)
    },
    isFromBlytzCollect() {
      const sender = this.message?.sender

      return Boolean(
        sender?.roles?.includes('super_admin') &&
        sender?.email === BLYTZCOLLECT_EMAIL
      )
    }
  }
})
</script>

<style scoped lang="scss">
.chat-message {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 10px 0;
}

.message-error {
  color: $customer-detail-error-text-color;
}

.message-date {
  width: 100%;
  margin: 0 0 16px;

  color: #fff;

  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.message-container {
  position: relative;

  width: 100%;
  max-width: 350px;
}

.message-content {
  display: flex;
  flex-direction: column;
  padding: 1rem;

  font-size: 0.8rem;

  border-radius: 10px;
}

.message-text {
  white-space: pre-wrap;
}

.message-time {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;
  width: 100%;

  .timestamp-text {
    text-align: right;
  }

  .message-status-icons {
    display: flex;
    gap: 4px;
    align-items: center;

    .status-icon-wrapper {
      position: relative;
      cursor: help;

      &:hover::after {
        content: attr(data-tooltip);
        position: absolute;
        bottom: 100%;
        left: 50%;
        transform: translateX(-50%);
        background: rgba(0, 0, 0, 0.8);
        color: white;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 12px;
        white-space: nowrap;
        z-index: 1000;
        margin-bottom: 5px;
      }

      &:hover::before {
        content: '';
        position: absolute;
        bottom: 100%;
        left: 50%;
        transform: translateX(-50%);
        border: 4px solid transparent;
        border-top-color: rgba(0, 0, 0, 0.8);
        z-index: 1000;
        margin-bottom: 1px;
      }
    }

    .status-icon {
      &.error {
        color: #dc3545;
      }

      &:hover {
        opacity: 0.8;
      }
    }
  }
}

.message-icon {
  margin-right: 5px;
  .message-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 25px;
    height: 25px;

    background-color: #f0f0f0;
    border-radius: 50%;
  }
  img {
    width: 20px;
  }
  .text-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;

    color: #000;
    font-weight: bolder;
    font-size: 0.8rem;

    background-color: #f0f0f0;
    border-radius: 50%;
  }
}

.inbound {
  .message-container {
    margin-left: auto;
    padding: 0 0 0 30px;
  }
}

.outbound {
  .message-container {
    margin-right: auto;
    padding: 0 30px 0 0;

    text-align: left;
  }
  div.attachment {
    justify-content: flex-end;
    max-width: 100%;
  }
}

.outbound .message-content {
  background: $chat-bubble-customer-start;
  background: linear-gradient(
    180deg,
    $chat-bubble-customer-start 0%,
    $chat-bubble-customer-end 100%
  );
}
.inbound .message-content {
  background: $chat-bubble-merchant-start;
  background: linear-gradient(
    180deg,
    $chat-bubble-merchant-start 0%,
    $chat-bubble-merchant-end 100%
  );
}
// Must stay AFTER .inbound: both selectors are two classes, so specificity ties and
// source order decides. Declared any earlier and the AI bubble silently renders green.
.ai-assistant .message-content {
  background: $chat-bubble-ai-start;
  background: linear-gradient(
    180deg,
    $chat-bubble-ai-start 0%,
    $chat-bubble-ai-end 100%
  );
}
.message-header {
  display: flex;
  align-items: center;
  width: 100%;
  margin: 0 0 8px;

  color: white;
  font-size: 0.9rem;
  text-transform: capitalize;
}

.attachment {
  display: flex;
  max-width: 250px;
  max-height: 150px;
  margin: 8px 0 0;

  cursor: pointer;
  img {
    max-width: 100%;
    max-height: 100%;
  }
}
.vcard {
  font-size: 2.25em;
}

.error {
  position: relative;

  display: flex;
  align-items: center;
  margin: 0 6px 0 0;
  padding: 0;

  color: #ad0017;
}

.error-message {
  display: flex;
  align-items: center;
  margin: 10px 0 0;

  color: #fff;
}
</style>
